/**
 * InsertSelectMapping — self-contained, read-only column-pairing card for
 * `INSERT … SELECT` (DML-10 / DML-11). Sits ABOVE the existing "Source query"
 * `ClauseCard` (nested `SelectRenderer`) — it annotates that card, it does
 * not replace or restructure it. Purely display: it visualizes the pairing
 * the statement already implies, it never emits or edits SQL, so there is no
 * user action here and no analytics event (mirrors the passive-indicator
 * precedent).
 *
 * Deliberately NOT threaded into `SelectColumnChip`/`SelectList` — those are
 * shared by ~15 SELECT surfaces; an insert-only pairing concern stays local
 * to `render/insert/`.
 */

import { AlertTriangle, ArrowLeft, Info } from 'lucide-react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import { vqeCopy } from '../../ux/vqeCopy';
import { ClauseCard } from '../select/ClauseCard';
import {
  extractSourceSelectItems,
  pairInsertColumns,
  type InsertSelectPair,
} from './insertSelectPairing';

export interface InsertSelectMappingProps {
  readonly targetColumns: readonly string[] | null;
  readonly sourceText: string;
  readonly options: ParseOptions;
}

function formatSourceExpression(source: { expression: string; alias: string | null }): string {
  return source.alias ? `${source.expression} AS ${source.alias}` : source.expression;
}

function MappingRow({ pair }: { readonly pair: InsertSelectPair }) {
  return (
    <li
      className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1"
      data-testid={`vqe-insert-select-mapping-row-${pair.index}`}
    >
      <span className="w-5 shrink-0 text-right font-mono text-[10px] text-muted-foreground">
        {pair.index + 1}
      </span>
      <span className="min-w-0 flex-1 truncate font-mono text-xs" data-testid={`vqe-insert-select-mapping-target-${pair.index}`}>
        {pair.target ?? '—'}
      </span>
      <ArrowLeft className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span
        className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground"
        data-testid={`vqe-insert-select-mapping-source-${pair.index}`}
      >
        {pair.source ? formatSourceExpression(pair.source) : '—'}
      </span>
    </li>
  );
}

export function InsertSelectMapping({ targetColumns, sourceText, options }: InsertSelectMappingProps) {
  const sourceItems = extractSourceSelectItems(sourceText, options);
  const result = pairInsertColumns(targetColumns, sourceItems);

  if (result.status === 'not-applicable') return null;

  return (
    <ClauseCard title="Column mapping" testId="vqe-insert-select-mapping">
      <div className="flex flex-col gap-2">
        {result.status === 'star-source' ? (
          <p
            className="flex items-start gap-1.5 text-xs text-muted-foreground"
            data-testid="vqe-insert-select-mapping-star-note"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{vqeCopy.hint.insertSelect.starNote(result.targetCount > 0 ? result.targetCount : null)}</span>
          </p>
        ) : (
          <>
            <ol
              className="m-0 flex list-none flex-col gap-1 p-0"
              data-testid="vqe-insert-select-mapping-rows"
            >
              {result.pairs.map((pair) => (
                <MappingRow key={pair.index} pair={pair} />
              ))}
            </ol>
            {result.status === 'count-mismatch' ? (
              <p
                className="flex items-start gap-1.5 text-xs text-warning"
                data-testid="vqe-insert-select-mapping-mismatch"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{vqeCopy.hint.insertSelect.countMismatch(result.targetCount, result.sourceCount)}</span>
              </p>
            ) : null}
            {result.status === 'no-target-list' ? (
              <p
                className="text-xs italic text-muted-foreground"
                data-testid="vqe-insert-select-mapping-implicit-note"
              >
                {vqeCopy.hint.insertSelect.implicitListNote(result.sourceCount)}
              </p>
            ) : null}
          </>
        )}
      </div>
    </ClauseCard>
  );
}
